import { HttpInterceptorFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, throwError, switchMap, take, filter } from 'rxjs';
import { AuthService } from './auth.service';

// Auth tokens live in httpOnly cookies and are sent automatically by the
// browser with every request because HttpService sets withCredentials: true.
// This interceptor's only job is to handle 401 responses by attempting a
// silent token refresh via the admin/refresh endpoint.
//
// Single-flight refresh with queuing: when several requests 401 at once
// (e.g. a dashboard firing parallel calls after the access cookie has
// expired), only the FIRST triggers a refresh. The rest wait on
// `refreshDone` and retry once the refresh resolves — instead of failing
// immediately, which was the old behaviour and logged users out on a
// burst of concurrent calls.

let isRefreshing = false;
// Emits `true` when a refresh succeeds, `false` when it fails. Queued
// requests subscribe, take(1), and either retry or propagate the error.
const refreshDone = new Subject<boolean>();

function shouldAttemptRefresh(error: HttpErrorResponse, url: string): boolean {
  return (
    error.status === 401 &&
    !url.includes('/auth/admin/login') &&
    !url.includes('/auth/admin/refresh')
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  const handleAuthFailure = (refreshError: unknown) => {
    // Refresh failed — clear user display data and redirect to login.
    localStorage.removeItem('user');
    authService.isAuthenticated.set(false);
    authService.currentUser.set(null);
    router.navigate(['/login']);
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!shouldAttemptRefresh(error, req.url)) {
        return throwError(() => error);
      }

      // A refresh is already in flight — queue this request behind it
      // rather than triggering a second refresh or failing outright.
      if (isRefreshing) {
        return refreshDone.pipe(
          filter((ok) => ok !== null && ok !== undefined),
          take(1),
          switchMap((ok): Observable<HttpEvent<unknown>> =>
            ok ? next(req) : throwError(() => error),
          ),
        );
      }

      isRefreshing = true;

      return authService.refreshToken().pipe(
        switchMap(() => {
          isRefreshing = false;
          refreshDone.next(true); // release queued requests to retry
          return next(req);
        }),
        catchError((refreshError) => {
          isRefreshing = false;
          refreshDone.next(false); // release queued requests to fail
          handleAuthFailure(refreshError);
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
